'use client';
import React, { useState } from 'react';
import { usePathname, useSelectedLayoutSegment } from 'next/navigation';
import Link from 'next/link';
import Logo from '@/app/components/shared/Logo';
import { getNavAppearance } from '@/navigation/appearancePolicy';
import { useScrollThreshold } from '@/hooks/useScrollThreshold';

import ReviewCartWidget from '../reviewCartWidget/ReviewCartWidget';
import UserSignInIcon from '../shared/UserSignInIcon';
import CartIcon from '../shared/CartIcon';
import HamburgerIcon from '../icons/HamburgerIcon';
import LogoutIcon from '../shared/LogoutIcon';

import { useCart } from '@/context/cartContext';
import { useIsMobile } from '@/hooks/useIsMobile';
import {
  DropdownMenu,
  HamburgerButton,
  IconsGroup,
  MenuColumn,
  Nav,
  NavContainer,
} from './components';

const NavBarClient = (props: {
  hideIcons?: boolean;
  isStatic?: boolean;
  isTransparent?: boolean;
  navigationLinks?: {
    leftColumn: React.ReactNode;
    rightColumn: React.ReactNode;
  };
  actionButtons?: React.ReactNode;
}) => {
  const {
    actionButtons,
    hideIcons = false,
    isStatic = false,
    isTransparent,
    navigationLinks,
  } = props;
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const isScrolled = useScrollThreshold({ threshold: 75 });
  const [isCartWidgetOpen, setIsCartWidgetOpen] = useState(false);
  const isMobile = useIsMobile();

  const pathname = usePathname();
  const selectedSegment = useSelectedLayoutSegment();

  const {
    isBackgroundWhite,
    requiresTransparentBackground,
    shouldUseWhiteIcons,
  } = getNavAppearance(pathname, {
    isMenuOpen,
    isScrolled,
    isTransparent,
    selectedSegment,
  });

  const { cart } = useCart();

  const renderWidget = () => (
    <>
      {isCartWidgetOpen && (
        <ReviewCartWidget
          isOpen={isCartWidgetOpen}
          onClose={() => setIsCartWidgetOpen(false)}
        />
      )}
    </>
  );

  return (
    <>
      {renderWidget()}
      <Nav isStatic={isStatic} isBackgroundWhite={isBackgroundWhite}>
        <NavContainer
          requiresTransparentBackground={requiresTransparentBackground}
        >
          <div className="flex items-center justify-center gap-2">
            <Link href="/" className="cursor-pointer">
              <Logo
                variant={
                  selectedSegment === 'activate'
                    ? 'white'
                    : shouldUseWhiteIcons
                      ? 'white'
                      : 'gradient'
                }
                height={38}
                width={110}
                sizes="110px"
                priority
              />
            </Link>
          </div>
          <div className="flex items-center justify-between gap-4 md:gap-8">
            {pathname !== '/review-your-cart' &&
              !requiresTransparentBackground &&
              actionButtons}

            <IconsGroup isHidden={hideIcons}>
              <UserSignInIcon
                isBackgroundWhite={
                  selectedSegment === 'activate' ? false : !shouldUseWhiteIcons
                }
              />

              <CartIcon
                isBackgroundWhite={
                  selectedSegment === 'activate' ? false : !shouldUseWhiteIcons
                }
                hasItems={cart.length > 0}
                onClick={() => setIsCartWidgetOpen(!isCartWidgetOpen)}
              />

              {requiresTransparentBackground && (
                <LogoutIcon
                  isBackgroundWhite={
                    selectedSegment === 'activate'
                      ? false
                      : !shouldUseWhiteIcons
                  }
                />
              )}

              <HamburgerButton
                ariaControlsId="navbar-menu"
                ariaLabel="Toggle navigation menu"
                isExpanded={isMenuOpen}
                onClick={() => setIsMenuOpen(v => !v)}
              >
                <HamburgerIcon
                  isOpen={isMenuOpen}
                  isWhite={
                    selectedSegment === 'activate' ? true : shouldUseWhiteIcons
                  }
                  className="cursor-pointer"
                />
              </HamburgerButton>
            </IconsGroup>
          </div>
        </NavContainer>

        <DropdownMenu
          isOpen={isMenuOpen}
          requiresTransparentBackground={requiresTransparentBackground}
        >
          <div className="flex flex-col px-4 pt-4 sm:px-6 lg:px-8">
            <div className="flex w-full flex-col pb-3 sm:flex-row sm:gap-16">
              <MenuColumn direction="left" isMobile={isMobile} delay={0.2}>
                {navigationLinks?.leftColumn}
              </MenuColumn>

              <MenuColumn direction="right" isMobile={isMobile} delay={0.3}>
                {navigationLinks?.rightColumn}
              </MenuColumn>
            </div>
          </div>
        </DropdownMenu>
      </Nav>
    </>
  );
};

export default NavBarClient;
